import { ReviewPeriodExpression } from "domain/Predictive/Enums/reviewPeriodExpressionPosition";
import { SBBox, SBGrid } from "modules/Commons";
import { TransferenceMode } from "modules/InventoryConfig/models/transferenceMode";
import { useFormContext } from "react-hook-form";
import ConfigGroupmentDivider from "../ConfigGroupmentDivider";
import ConfigTextField from "../ConfigTextField";
import ConfigTransferenceMode from "./ConfigTransferenceMode";
import CreateInventoryStatus from "./CreateInventoryStatus";
import SupplyReviewPeriod from "./SupplyReviewPeriod";
import ConfigEmergencialCoverageMode from "./ConfigEmergencialCoverageMode";
import { EmergencialCoverageMode } from "modules/EmergencialLeadTime/models/emergencialCoverageMode";
import { RecommendationMode } from "modules/InventoryConfig/models/recommendationMode";
import ConfigRecommendationMode from "./ConfigRecommendationMode";
import { useContext, useEffect, useState } from "react";
import { SBTranslate } from "i18n";
import { TipsContext } from "views/ConfigsView/ConfigTips";
import { RecommendationSecurityLimitFrequency } from "modules/SecurityLimit/RecommendationSecurityLimitFrequency";
import ConfigRecommendationSecurityLimitFrequency from "./ConfigRecommendationSecurityLimitFrequency";

export default ({
	fetchPropError,
	inventoryFormulas,
	stores
}: CreateInventorySupplyProps) => {
	const tipsContext = useContext(TipsContext);
	const { setValue, getValues } = useFormContext();

	const unitsText = <>{SBTranslate("unit", { count: 2 })}</>;
	const daysText = <>{SBTranslate("day", { count: 2 })}</>;
	const cyclesText = <>{SBTranslate("cycle", { count: 2 })}</>;

	const [recommendationSecurityLimitPeriod, setRecommendationSecurityLimitPeriod] = useState<number | null>(
		getValues("recommendationSecurityLimitPeriod") ? getValues("recommendationSecurityLimitPeriod") : null
	);

	const onSetRecommendationSecurityLimitPeriod = (value: string) => {
		setValue("recommendationSecurityLimitPeriod", value.positiveNumber());
		setRecommendationSecurityLimitPeriod(value.positiveNumber());
	}

	const onSetRecommendationSecurityLimitFrequency = (v: RecommendationSecurityLimitFrequency | null) => {
		setValue("recommendationSecurityLimitFrequency", v);
	};

	const onSetReviewPeriod = (v: any) => {
		setValue(
			"reviewPeriodExpression",
			ReviewPeriodExpression.Modifiers.getValidExpressions(v)
		);
	};

	const onSetSourcing = (v: any) => {
		setValue("sourcingID", v.sourcingID);
		setValue("sourcingType", v.sourcingType);
	};

	const onSetTransfernceMode = (v: TransferenceMode | null) => {
		setValue("transferenceMode", v);
	};

	const onSetEmergencialCoverageMode = (v: EmergencialCoverageMode | null) => {
		setValue("emergencialCoverageMode", v);
	};

	const [recommendationMode, setRecommendationMode] = useState<RecommendationMode | null>(null);

	const onSetRecommendationMode = (v: RecommendationMode | null) => {
		setValue("recommendationMode", v);
		setRecommendationMode(v);
	};

	const getEmergencialCoverageMode = getValues("emergencialCoverageMode");
	const getRecommendationSecurityLimitFrequency = getValues("recommendationSecurityLimitFrequency") ?? RecommendationSecurityLimitFrequency.daily;
	const getRecommendationMode = getValues("recommendationMode");
	const initialValue = getValues("transferenceMode");
	const initialSupplyValue = { sourcingID: getValues("sourcingID"), sourcingType: getValues("sourcingType") };

	useEffect(() => {
		setValue("recommendationSecurityLimitPeriod", recommendationSecurityLimitPeriod);
	}, [recommendationSecurityLimitPeriod]);

	return (
		<SBBox height={1}>
			<ConfigGroupmentDivider title={SBTranslate("supplyConfig")} />
			<SBGrid container spacing={4} marginBottom={2}>
				<SBGrid item xs={4}>
					<ConfigTextField
						section="supply"
						type="integer"
						prop="moq"
						title={SBTranslate("moq")}
						endAdornment={unitsText}
						helperText={fetchPropError("moq")}
					/>
				</SBGrid>
				<SBGrid item xs={4}>
					<ConfigTextField
						section="supply"
						type="integer"
						prop="lotSize"
						title={SBTranslate("lotSize")}
						endAdornment={unitsText}
						helperText={fetchPropError("lotSize")}
					/>
				</SBGrid>
				<SBGrid item xs={4}>
					<ConfigTextField
						section="supply"
						type="integer"
						prop="capacity"
						title={SBTranslate("capacity")}
						endAdornment={unitsText}
						helperText={fetchPropError("capacity")}
					/>
				</SBGrid>

				<SBGrid item xs={4}>
					<ConfigTextField
						section="supply"
						type="integer"
						prop="leadTime"
						title={SBTranslate("leadTime")}
						endAdornment={daysText}
						helperText={fetchPropError("leadTime")}
					/>
				</SBGrid>
				<SBGrid item xs={4}>
					<ConfigTextField
						section="supply"
						type="integer"
						prop="emergencialLeadTime"
						title={SBTranslate("emergencialLeadTime")}
						endAdornment={daysText}
						helperText={fetchPropError("emergencialLeadTime")}
					/>
				</SBGrid>

				<SBGrid item xs={4}>
					<ConfigTextField
						section="supply"
						type="integer"
						title={SBTranslate("securityLimit")}
						endAdornment={<ConfigRecommendationSecurityLimitFrequency
							value={getRecommendationSecurityLimitFrequency}
							onUpdate={onSetRecommendationSecurityLimitFrequency}
						/>}
						value={recommendationSecurityLimitPeriod}
						onClick={() => {
							tipsContext.showTip("SecurityLimit");
						}}
						onChange={(e) => onSetRecommendationSecurityLimitPeriod(e.target.value)}
					/>
				</SBGrid>
			</SBGrid>
			<SBGrid item xs={6} marginBottom={2}>
				<ConfigEmergencialCoverageMode
					value={getEmergencialCoverageMode}
					onUpdate={onSetEmergencialCoverageMode}
				/>
			</SBGrid>
			<SBGrid item xs={12} marginBottom={2}>
				<ConfigTransferenceMode
					value={initialValue}
					onUpdate={onSetTransfernceMode}
				/>
			</SBGrid>
			<SBGrid marginBottom={2}>
				<CreateInventoryStatus
					sourcingValue={initialSupplyValue}
					onUpdate={onSetSourcing}
					inventoryFormulas={inventoryFormulas}
					stores={stores}
					fetchPropError={fetchPropError}
				/>
			</SBGrid>
			<SBGrid container spacing={4} marginBottom={2}>
				<SBGrid item xs={6}>
					<ConfigRecommendationMode
						value={getRecommendationMode}
						onUpdate={onSetRecommendationMode}
					/>
				</SBGrid>
				<SBGrid item xs={6}>
					<ConfigTextField
						section="supply"
						type="integer"
						prop="numberOfCyclesPerReview"
						title={SBTranslate("numberOfCyclesPerReview")}
						endAdornment={cyclesText}
						helperText={fetchPropError("numberOfCyclesPerReview")}
					/>
				</SBGrid>
			</SBGrid>
			<SupplyReviewPeriod
				recommendationMode={recommendationMode}
				fetchPropError={fetchPropError}
				onUpdate={onSetReviewPeriod}
			/>
		</SBBox>
	);
};

export interface CreateInventorySupplyProps {
	onUpdate: (supply: {
		moq: number | null;
		lotSize: number | null;
		capacity: number | null;
		leadTime: number | null;
		numberOfCyclesPerReview: number | null;
		recommendationSecurityLimitPeriod: number | null;
		reviewPeriodExpression: string | null;
		sourcingID: string | null;
		sourcingType: number | null;
	}) => void;
	values: {
		moq: number;
		lotSize: number;
		capacity: number;
		leadTime: number;
		numberOfCyclesPerReview: number | null;
		recommendationSecurityLimitPeriod: number;
		reviewPeriodExpression: string | null;
		sourcingID: string | null;
		sourcingType: string | null;
	};
	fetchPropError: any;
	inventoryFormulas: any;
	stores: any;
}
